import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { TFunction } from 'i18next';
import { Button } from '../ui/button';
import StarRating, { scoreLabel } from './StarRating';
import {
  RATING_REASONS_NEGATIVE,
  RATING_REASONS_POSITIVE,
  RATING_DETAILS_NEGATIVE,
  RATING_DETAILS_POSITIVE,
  NEGATIVE_MAX_SCORE,
  type RatingReason,
  type RatingDetail,
  type RateInterventionBody,
} from '../../api/interventionRatingApi';

// Literal translation calls only (static i18n guard).
export const reasonLabel = (t: TFunction, reason: RatingReason): string => {
  switch (reason) {
    case 'late':
      return t('Late for the time slot');
    case 'incomplete':
      return t('Incomplete work');
    case 'not_cleaned':
      return t('Site left dirty');
    case 'communication':
      return t('Poor communication');
    case 'instructions_ignored':
      return t('Instructions not followed');
    case 'damage':
      return t('Damage observed');
    case 'punctual':
      return t('Punctuality');
    case 'quality_work':
      return t('Quality work');
    case 'clean_site':
      return t('Clean site');
    case 'good_communication':
      return t('Good communication');
    case 'helpful_advice':
      return t('Helpful advice');
    case 'speed':
      return t('Speed');
  }
};

export const detailLabel = (t: TFunction, detail: RatingDetail): string => {
  switch (detail) {
    case 'late_arrival':
      return t('The technician arrived late for the time slot.');
    case 'not_finished':
      return t('The intervention was not completed.');
    case 'store_not_restored':
      return t('The store was not restored to its original state.');
    case 'result_mismatch':
      return t('The result does not match the request.');
    case 'work_as_requested':
      return t('The intervention matches the request.');
    case 'punctual_professional':
      return t('Punctual and professional technician.');
    case 'store_left_clean':
      return t('The store was left clean after the intervention.');
    case 'fast_well_explained':
      return t('Fast and well-explained intervention.');
  }
};

export const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

interface RatingFormProps {
  technicianName: string;
  issueKey: string;
  initialScore?: number;
  // Prefill when editing an already-sent rating.
  initialReasons?: RatingReason[];
  initialDetail?: RatingDetail | null;
  onSubmit: (body: RateInterventionBody) => void;
  onLater: () => void;
  isPending?: boolean;
}

const RatingForm = ({
  technicianName,
  issueKey,
  initialScore = 0,
  initialReasons = [],
  initialDetail = null,
  onSubmit,
  onLater,
  isPending = false,
}: RatingFormProps) => {
  const { t } = useTranslation();
  const [score, setScore] = useState(initialScore);
  const [reasons, setReasons] = useState<RatingReason[]>(initialReasons);
  const [detail, setDetail] = useState<RatingDetail | null>(initialDetail);
  const positive = score > NEGATIVE_MAX_SCORE;
  const reasonCodes: readonly RatingReason[] = positive ? RATING_REASONS_POSITIVE : RATING_REASONS_NEGATIVE;
  const detailCodes: readonly RatingDetail[] = positive ? RATING_DETAILS_POSITIVE : RATING_DETAILS_NEGATIVE;

  // Crossing the 3-star boundary switches the code set: picked codes no longer apply.
  const pickScore = (next: number) => {
    if ((next > NEGATIVE_MAX_SCORE) !== positive) {
      setReasons([]);
      setDetail(null);
    }
    setScore(next);
  };

  const toggleReason = (reason: RatingReason) =>
    setReasons((current) => (current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason]));

  const submit = () => onSubmit({ score, reasons, detail });

  return (
    <div className="flex flex-col gap-5 overflow-y-auto px-5 pb-6 pt-2 max-h-[85vh]">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-xl font-semibold text-gray-800" aria-hidden="true">
          {initials(technicianName)}
        </div>
        <h2 className="text-xl font-semibold text-gray-900">{t('How did the intervention go?')}</h2>
        <p className="text-sm text-gray-500">{technicianName} · {issueKey}</p>
        <StarRating value={score} onChange={pickScore} size="lg" />
        <p className="min-h-[1.5rem] text-base font-medium text-gray-900">{score > 0 ? scoreLabel(t, score) : ''}</p>
      </div>

      {score > 0 && (
        <>
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-900">{positive ? t('What did you like?') : t('What went wrong?')}</p>
            <div className="flex flex-wrap gap-2">
              {reasonCodes.map((reason) => {
                const selected = reasons.includes(reason);
                return (
                  <button
                    key={reason}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleReason(reason)}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      selected ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-gray-900 hover:border-gray-500'
                    }`}
                  >
                    {reasonLabel(t, reason)}
                  </button>
                );
              })}
            </div>
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold text-gray-900">
              {t('Specify')} <span className="font-normal text-gray-500">— {t('optional')}</span>
            </legend>
            <div className="flex flex-col gap-2">
              {detailCodes.map((code) => (
                <label
                  key={code}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm ${
                    detail === code ? 'border-black' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="rating-detail"
                    value={code}
                    checked={detail === code}
                    onChange={() => setDetail(code)}
                    className="h-4 w-4 accent-black"
                  />
                  <span>{detailLabel(t, code)}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </>
      )}

      <div className="flex gap-2 rounded-xl bg-gray-50 p-4 text-xs text-gray-500">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="space-y-1.5">
          <p>{t('The rating is used for supplier quality monitoring. The technician only sees an aggregated average, never your individual rating.')}</p>
          <p>{t('No free text: only predefined reasons are transmitted, without personal data.')}</p>
          <p>{t('Kept for 12 months. You can change or delete your rating from this page at any time.')}</p>
          <Link to="/privacy-policy" className="font-medium text-gray-700 underline">
            {t('Privacy Policy')}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button type="button" variant="secondary" size="lg" onClick={onLater} disabled={isPending}>
          {t('Later')}
        </Button>
        <Button type="button" size="lg" onClick={submit} disabled={score === 0 || isPending}>
          {t('Send rating')}
        </Button>
      </div>
    </div>
  );
};

export default RatingForm;
